import { useGetAccounts, useSplitTests } from '@api/lendingAPI/lapiHooks';
import { Constants } from '@constants';
import { getAccountIsLoanEnded } from '@helpers/Accounts/getAccountIsLoanEnded';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { IconExclamationTriangleFilled, IconHomeFilled } from '@iwoca/icons';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { useQuery } from '@tanstack/react-query';
import { differenceInMonths } from 'date-fns';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import messages from './DeclinedView.messages';
import styles from './DeclinedView.module.css';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import InternationalisedName from '../../../myAccount/multiLoanDashboard/dashboardComponents/InternationalisedName';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../pageStructure/mainContentPending';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import { DECLINED_APPLICATION_REAPPLY } from '../MyOffers.actions';
import { RequestedProduct } from '../MyOffers.types';
import { CreditCompassDeclineView } from './creditCompassDeclineView/CreditCompassDeclineView';
import { useGetCreditCardOffer } from '../../creditCard/hooks/useGetCreditCardOffer';
import { CreditCardOffer } from '../../creditCard/offers/CreditCardOffer';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    reapplyFlexi: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: {
                pathname: '/apply-flexi-loan/'
            }
        }),
    declinedReapply: (requestedProduct: RequestedProduct) =>
        dispatch({
            type: DECLINED_APPLICATION_REAPPLY,
            currentRequestedProduct: requestedProduct
        })
});

const DeclinedView = ({
    reapplyFlexi,
    declinedReapply
}: ReturnType<typeof mapDispatchToProps>) => {
    const stateKey = useStateKey();

    const {
        data: application,
        isLoading: applicationLoading,
        isError: applicationError
    } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            })
    });

    const {
        creditCardOffer,
        getCreditCardOfferError,
        getCreditCardOfferLoading
    } = useGetCreditCardOffer();

    const creditCompassSplitTestBranch = useSplitTests('credit_compass_test');
    const { data: accounts, isLoading: accountsLoading } = useGetAccounts();

    if (applicationLoading || accountsLoading || getCreditCardOfferLoading) {
        return <MainContentPending />;
    }

    if (applicationError || !application.data || getCreditCardOfferError) {
        return <FallbackErrorModal />;
    }

    const hasEndedLoan = accounts?.accounts?.some((account) =>
        getAccountIsLoanEnded(account)
    );

    if (creditCardOffer && !isEmpty(creditCardOffer)) {
        return <CreditCardOffer />;
    }

    if (creditCompassSplitTestBranch && !hasEndedLoan) {
        return (
            <MainSectionWrapper className={styles.wrapper}>
                <CreditCompassDeclineView />
            </MainSectionWrapper>
        );
    }

    const status = application.data.status;
    const statusDate = application.data.status_date;
    const declineStatus = application.data.decline_status;
    const requestedProduct = application.data.requested_products?.[0];

    const country = getCountry();

    const getReapplyEligibility = () => {
        if (status !== 'declined' || !declineStatus || !statusDate)
            return false;

        const monthsSinceStatusChange = differenceInMonths(
            new Date(),
            statusDate!
        );

        if (
            monthsSinceStatusChange >= 6 ||
            (country === 'UK' &&
                monthsSinceStatusChange >= 3 &&
                declineStatus === 'Soft Declined')
        )
            return true;

        return false;
    };

    const reapply = () => {
        if (requestedProduct?.product_type === 'merchant_cash_advance') {
            declinedReapply(requestedProduct);
        } else {
            reapplyFlexi();
        }
    };

    const shouldSeeReapplyCard = getReapplyEligibility() && country === 'UK';

    return shouldSeeReapplyCard ? (
        <ReapplyCard ctaOnClick={reapply} />
    ) : (
        <DeclinedCard
            ctaOnClick={reapply}
            isCTAVisible={getReapplyEligibility()}
        />
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(DeclinedView);

const DeclinedCard = ({
    ctaOnClick,
    isCTAVisible
}: {
    ctaOnClick: () => void;
    isCTAVisible: boolean;
}) => {
    return (
        <MainSectionWrapper className={styles.wrapper}>
            <div className={styles.card}>
                <div className={styles.iconContainer}>
                    <IconExclamationTriangleFilled
                        className={styles.titleIcon}
                    />
                </div>

                <div className={styles.title}>
                    <FormattedMessage {...messages.title} />
                </div>

                <div className={styles.bodyText}>
                    <FormattedMessage
                        data-test-id="declined-body-text"
                        {...messages.description}
                        values={{
                            phoneNumber: (
                                <a
                                    href={Constants.PHONE_NUMBER.tel}
                                    className={styles.phoneNumber}
                                >
                                    {Constants.PHONE_NUMBER.display}
                                </a>
                            )
                        }}
                    />
                </div>

                {isCTAVisible && (
                    <Button
                        size="large"
                        variant="primary"
                        className={styles.reApplyCTA}
                        data-testid="reapply-cta"
                        onClick={ctaOnClick}
                    >
                        <FormattedMessage {...messages.reApplyButtonText} />
                    </Button>
                )}
            </div>
        </MainSectionWrapper>
    );
};

const ReapplyCard = ({ ctaOnClick }: { ctaOnClick: () => void }) => {
    return (
        <MainSectionWrapper
            className={styles.wrapper}
            data-testid="reapply-card"
        >
            <div className={styles.card}>
                <div className={styles.iconContainer}>
                    <IconHomeFilled className={styles.titleIcon} />
                </div>

                <div className={styles.title}>
                    <FormattedMessage
                        {...messages.reapplyTitle}
                        values={{ name: <InternationalisedName /> }}
                    />
                </div>

                <div className={styles.bodyText}>
                    <FormattedMessage
                        data-test-id="declined-reapply-body-text"
                        {...messages.reapplyDescription}
                        values={{
                            phoneNumber: (
                                <a
                                    href={Constants.PHONE_NUMBER.tel}
                                    className={styles.phoneNumber}
                                >
                                    {Constants.PHONE_NUMBER.display}
                                </a>
                            )
                        }}
                    />
                </div>

                <Button
                    size="large"
                    variant="primary"
                    className={styles.reApplyCTA}
                    data-testid="reapply-cta"
                    onClick={ctaOnClick}
                >
                    <FormattedMessage {...messages.reApplyButtonText} />
                </Button>
            </div>
        </MainSectionWrapper>
    );
};
